import React, { useState, useRef, useEffect } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   FormControl,
   FormLabel,
   Input,
   Select,
   Textarea,
   useToast,
   useColorMode,
   useColorModeValue,
   Box,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import { getAccount } from '../../../../redux/actions/account';
import { useDispatch, useSelector } from 'react-redux';
import { addJournalVoucher, updateJournalVoucher } from '../../../../redux/actions/journalVoucher';
import AccountSelect from 'components/AccountSelect/AccountSelect';
import { useUser, useGlobal, useDaybook } from 'contexts';
import { Plus } from 'lucide-react';
import { renderDynamicDropdown } from 'components/ui/DynamicDropdown';

const JournalVoucherForm = ({ isOpen, onClose, isEditMode, currentRow, onSave }) => {
   const dispatch = useDispatch();
   const toast = useToast();
   const accounts = useSelector((state) => state.account.list || []);
   const { daybooks } = useDaybook();
   const filteredDaybooks = daybooks.filter((daybook) => daybook.type === 18);
   const textColor = useColorModeValue('#9E9E9E', 'white');
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const [hasUserModified, setHasUserModified] = useState(false);
   const [isSaving, setIsSaving] = useState(false);

   const dateInputRef = useRef(null);
   const daybookRef = useRef(null);
   const notesRef = useRef(null);
   const saveRef = useRef(null);
   const accountRef = useRef([]);
   const NumberRef = useRef([]);
   const typeRef = useRef([]);

   const initialEntries = [
      {
         account: '',
         amount: '',
         type: '0',
      },
   ];

   const [formData, setFormData] = useState({
      date: new Date().toLocaleDateString('en-GB'),
      title: '',
      entries: initialEntries,
      notes: '',
      daybookId: '',
   });
   const accountDropdownRefs = useRef([]);

   useEffect(() => {
      if (isOpen && dateInputRef.current) {
         const timer = setTimeout(() => {
            dateInputRef.current.focus();
         }, 100);

         return () => clearTimeout(timer);
      }
   }, [isOpen]);

   const addEntry = (shouldFocusAccount = false) => {
      const newIndex = formData.entries.length;

      setFormData((prev) => ({
         ...prev,
         entries: [
            ...prev.entries,
            {
               account: '',
               amount: '',
               type: '1',
            },
         ],
      }));

      if (shouldFocusAccount) {
         setTimeout(() => {
            if (accountDropdownRefs.current[newIndex]) {
               accountDropdownRefs.current[newIndex].focus();
            }
         }, 150);
      }
   };

   const { user } = useUser();
   const { userGlobalSettingsData } = useGlobal();
   const shouldSendCompanyidAccount = userGlobalSettingsData?.[0]?.askCompany?.account || false;

   useEffect(() => {
      if (user && isOpen) {
         dispatch(
            getAccount({
               compId: user?.loginhistory?.companyId,
               status: true,
               ...(shouldSendCompanyidAccount && { companyIds: user?.loginhistory?.companyId }),
            }),
         );
      }
   }, [dispatch, user?.loginhistory?.companyId, isOpen]);

   useEffect(() => {
      if (isEditMode && currentRow) {
         let entries = [...initialEntries];

         // Set daybookId from currentRow if available
         const daybookId = currentRow.daybookId ? currentRow.daybookId.toString() : '';

         if (currentRow.jvEntries && Array.isArray(currentRow.jvEntries)) {
            entries = currentRow.jvEntries.map((entry) => ({
               account: entry.accountId ? entry.accountId.toString() : '',
               amount: entry.amount ? entry.amount.toString() : '',
               type: entry.txnType !== undefined ? entry.txnType.toString() : '0',
            }));
         } else if (currentRow.entries) {
            try {
               const parsedEntries =
                  typeof currentRow.entries === 'string' ? JSON.parse(currentRow.entries) : currentRow.entries;
               if (Array.isArray(parsedEntries)) {
                  entries = parsedEntries.map((entry) => ({
                     account: entry.account || entry.accountId?.toString() || '',
                     amount: entry.amount?.toString() || '',
                     type: entry.type || entry.txnType?.toString() || '0',
                  }));
               }
            } catch (e) {
               console.error('Failed to parse entries JSON:', e);
            }
         }

         setFormData({
            date: currentRow.txnDate
               ? new Date(currentRow.txnDate).toLocaleDateString('en-GB')
               : new Date().toLocaleDateString('en-GB'),
            title: currentRow.title || '',
            entries: entries.length > 0 ? entries : [...initialEntries],
            notes: currentRow.notes || '',
            daybookId: daybookId,
         });
      } else {
         // Set first daybook as default when not in edit mode
         // Check if filteredDaybooks has items and get the correct ID property
         let defaultDaybookId = '';

         if (filteredDaybooks.length > 0 && filteredDaybooks[0]) {
            // Try different possible property names
            defaultDaybookId = (
               filteredDaybooks[0].value?.toString() ||
               filteredDaybooks[0].id?.toString() ||
               filteredDaybooks[0]._id?.toString() ||
               ''
            );
         }

         setFormData({
            date: new Date().toLocaleDateString('en-GB'),
            title: '',
            entries: [...initialEntries],
            notes: '',
            daybookId: defaultDaybookId,
         });
         setHasUserModified(false);
      }
   }, [isEditMode, currentRow, isOpen]);

   const lastInputRef = useRef(null);

   const calculateTotals = () => {
      let totalDebit = 0;
      let totalCredit = 0;

      formData.entries.forEach((entry) => {
         const amount = parseFloat(entry.amount) || 0;
         if (entry.type === '1') {
            totalDebit += amount;
         } else {
            totalCredit += amount;
         }
      });

      return { totalDebit, totalCredit, difference: Math.abs(totalDebit - totalCredit) };
   };

   const { totalDebit, totalCredit, difference } = calculateTotals();

   const handleKeyDown = (e, index) => {
      if ((e.key === 'Tab' || e.key === 'Enter') && !e.shiftKey && index === formData.entries.length - 1) {
         if (formData.entries.length === 1) {
            e.preventDefault();
            const newIndex = formData.entries.length;
            const newEntry = {
               account: '',
               amount: formData.entries[0].amount || '',
               type: formData.entries[0].type === '0' ? '1' : '0',
            };

            setFormData((prev) => ({
               ...prev,
               entries: [...prev.entries, newEntry],
            }));

            setTimeout(() => {
               if (accountRef.current[newIndex]) {
                  accountRef.current[newIndex].focus();
               }
            }, 150);
         } else if (totalDebit !== totalCredit && difference > 0) {
            e.preventDefault();
            addBalancingEntry(true);
            setTimeout(() => {
               if (accountRef.current[index + 1]) {
                  accountRef.current[index + 1].focus();
               }
            }, 150);
         } else {
            if (notesRef.current) {
               notesRef.current?.focus();
            }
         }
      }
   };

   const addBalancingEntry = (shouldFocusAccount = false) => {
      const { totalDebit, totalCredit, difference } = calculateTotals();

      if (difference === 0) return;

      const newIndex = formData.entries.length;
      const newEntry = {
         account: '',
         amount: difference.toString(),
         type: totalDebit > totalCredit ? '0' : '1',
      };

      setFormData((prev) => ({
         ...prev,
         entries: [...prev.entries, newEntry],
      }));

      if (shouldFocusAccount) {
         setTimeout(() => {
            if (accountDropdownRefs.current[newIndex]) {
               accountDropdownRefs.current[newIndex].focus();
            }
         }, 150);
      }
   };

   const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
         ...prev,
         [name]: value,
      }));
   };

   const handleEntryChange = (index, field, value) => {
      const updatedEntries = [...formData.entries];
      updatedEntries[index][field] = value;

      if (field === 'amount' && formData.entries.length >= 2 && index < 2 && !hasUserModified) {
         const otherIndex = index === 0 ? 1 : 0;
         if (updatedEntries[index].type !== updatedEntries[otherIndex].type) {
            updatedEntries[otherIndex].amount = value;
         }
      }

      setFormData((prev) => ({
         ...prev,
         entries: updatedEntries,
      }));

      if (field === 'type') {
         setTimeout(() => {
            const { totalDebit, totalCredit, difference } = calculateTotals();
            if (difference > 0) {
               addBalancingEntry(true);
            }
         }, 100);
      }
   };

   const handleAmountFocus = (index) => {
      if (index < 2 && !hasUserModified) {
         setHasUserModified(true);
      }
   };

   const handleDaybookChange = (value) => {
      setFormData((prev) => ({
         ...prev,
         daybookId: value,
      }));
   };

   const removeEntry = (index) => {
      if (formData.entries.length <= 1) {
         toast({
            title: 'Cannot remove entry',
            description: 'A journal voucher must have at least one entry',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         return;
      }

      setFormData((prev) => ({
         ...prev,
         entries: prev.entries.filter((_, i) => i !== index),
      }));
   };

   const isBalanced = totalDebit === totalCredit;

   const formatDateForInput = (dateStr) => {
      if (!dateStr || typeof dateStr !== 'string') {
         const today = new Date();
         return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
            today.getDate(),
         ).padStart(2, '0')}`;
      }

      try {
         const [day, month, year] = dateStr.split('/');
         return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      } catch (error) {
         console.error('Error formatting date for input:', error);
         const today = new Date();
         return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
            today.getDate(),
         ).padStart(2, '0')}`;
      }
   };

   const formatDateFromInput = (inputDateStr) => {
      try {
         const [year, month, day] = inputDateStr.split('-');
         return `${day}/${month}/${year}`;
      } catch (error) {
         console.error('Error formatting date from input:', error);
         return new Date().toLocaleDateString('en-GB');
      }
   };

   const handleSave = async () => {
      // Prevent multiple submissions
      if (isSaving) {
         console.log('Already saving, please wait...');
         return;
      }

      // Validation checks (keep your existing validations)
      if (!formData.date) {
         toast({
            title: 'Date is required',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         return;
      }

      if (formData.entries.length < 2) {
         toast({
            title: 'Incomplete entries',
            description: 'Journal voucher must have at least two entries',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         return;
      }

      if (!isBalanced) {
         toast({
            title: 'Journal not balanced',
            description: 'Total debit must equal total credit',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         return;
      }

      const hasEmptyAccount = formData.entries.some((entry) => !entry.account);
      if (hasEmptyAccount) {
         toast({
            title: 'Invalid entry',
            description: 'All entries must have an account selected',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         return;
      }

      const hasInvalidAmount = formData.entries.some((entry) => isNaN(parseFloat(entry.amount)));
      if (hasInvalidAmount) {
         toast({
            title: 'Invalid amount',
            description: 'All amounts must be valid numbers',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         return;
      }

      // Set saving state to prevent multiple submissions
      setIsSaving(true);

      try {
         const formattedData = {
            daybookId: parseInt(formData.daybookId),
            companyId: parseInt(localStorage.getItem('companyId')),
            txnDate: formatDateForInput(formData.date),
            totalAmount: totalDebit,
            notes: formData.notes,
            jvEntries: formData.entries.map((entry) => ({
               accountId: parseInt(entry.account),
               amount: parseFloat(entry.amount),
               txnType: parseInt(entry.type),
            })),
            docNumber: currentRow?.docNumber?.toString(),
         };

         let result;
         if (isEditMode && currentRow?.docNumber) {
            result = await dispatch(updateJournalVoucher(formattedData));
         } else {
            result = await dispatch(addJournalVoucher(formattedData));
         }

         // Check if the API call was successful
         if (result?.error) {
            throw new Error(result.error.message || 'Failed to save journal voucher');
         }

         // Success actions
         onSave();
         onClose();
      } catch (error) {
         console.error('Save error:', error);
         toast({
            title: 'Error saving journal voucher',
            description: error.message || 'Please try again',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      } finally {
         // Always reset saving state, even if there's an error
         setIsSaving(false);
      }
   };

   return (
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
         <ModalOverlay className="bg-black/40" />
         <ModalContent
            className={`rounded-xl shadow-2xl max-h-[90vh] overflow-hidden ${isDark ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'
               }`}>
            {/* Clean Header */}
            <ModalHeader
               className={`py-4 px-6 border-b ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-800'
                  }`}>
               <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                     <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-600' : 'bg-blue-500'}`}>
                        <span className="text-white text-lg">
                           <Plus />
                        </span>
                     </div>
                     <div>
                        <h2 className="text-xl font-semibold">
                           {isEditMode ? 'Edit Journal Voucher' : 'New Journal Voucher'}
                        </h2>
                     </div>
                  </div>
               </div>
            </ModalHeader>

            <ModalCloseButton
               className={`mt-2 mr-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}
            />

            <ModalBody className={`p-6 overflow-y-auto custom-scrollbar ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
               <div className="space-y-6">
                  {/* Date Section */}
                  <div
                     className={`rounded-lg p-4 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                        }`}>
                     <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        Transaction Details
                     </h3>

                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormControl isRequired>
                           <FormLabel className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              Date
                           </FormLabel>
                           <Input
                              ref={dateInputRef}
                              type="date"
                              name="date"
                              value={formatDateForInput(formData.date)}
                              onChange={(e) =>
                                 setFormData((prev) => ({
                                    ...prev,
                                    date: formatDateFromInput(e.target.value),
                                 }))
                              }
                              onKeyDown={(e) => {
                                 if ((e.key === "Enter" || e.key === "Tab") && !e.shiftKey) {
                                    e.preventDefault();
                                    daybookRef.current?.focus();
                                 }
                              }}
                              className={`rounded-md ${isDark
                                 ? 'bg-gray-700 border-gray-600 text-white'
                                 : 'bg-white border-gray-300 text-gray-900'
                                 }`}
                           />
                        </FormControl>
                        <Box mt={8}>
                           {renderDynamicDropdown(
                              'daybookId',
                              'Daybook',
                              filteredDaybooks,
                              {
                                 ref: daybookRef,
                                 value: formData.daybookId,
                                 onChange: handleDaybookChange,
                                 showCustomNoOptions: true,
                                 openfrom: 'daybook',
                                 onKeyDown: (e) => {
                                    if (e.key === 'Enter' || e.key === 'Tab') {
                                       e.preventDefault();
                                       if (e.shiftKey) {
                                          dateInputRef?.current?.focus();
                                       } else {
                                          accountRef?.current[0]?.focus();
                                       }
                                    }
                                 },
                              },
                              {},
                              textColor,
                              true,
                              'Select Daybook',
                              true,
                              undefined,
                              false,
                           )}
                        </Box>
                     </div>
                  </div>

                  {/* Entries Section */}
                  <div
                     className={`rounded-lg p-4 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                        }`}>
                     <div className="flex items-center justify-between mb-4">
                        <h3 className={`text-lg font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                           Journal Entries
                        </h3>
                        <div
                           className={`px-3 py-1 rounded text-sm ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                              }`}>
                           {formData.entries.length} entries
                        </div>
                     </div>

                     {/* Header Grid */}
                     <div
                        className={`grid grid-cols-12 gap-3 px-3 py-3 rounded-md mb-3 text-sm font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                           }`}>
                        <div className="col-span-5">Account</div>
                        <div className="col-span-3">Amount</div>
                        <div className="col-span-2">Type</div>
                        <div className="col-span-2 text-center">Action</div>
                     </div>

                     {/* Entry Rows */}
                     <div className="space-y-3">
                        {formData.entries.map((entry, index) => (
                           <div
                              key={index}
                              className={`grid grid-cols-12 gap-3 p-3 rounded-md border ${isDark ? 'bg-gray-750 border-gray-600' : 'bg-white border-gray-200'
                                 }`}>
                              {/* Account Select */}
                              <div className="col-span-5">
                                 <AccountSelect
                                    ref={(el) => { accountRef.current[index] = el }}
                                    name="account"
                                    options={accounts}
                                    label="Account"
                                    value={Number(entry.account)}
                                    errors={{}}
                                    errorName="header.accountId"
                                    isClearable
                                    isSearchable
                                    onChange={(value) => {
                                       handleEntryChange(index, 'account', value);
                                    }}
                                    onKeyDown={(e) => {
                                       if (e.key === 'Enter' || e.key === 'Tab') {
                                          e.preventDefault();
                                          if (e.shiftKey) {
                                             if (index === 0) {
                                                daybookRef.current?.focus();
                                             } else {
                                                NumberRef?.current[index - 1]?.focus();
                                             }
                                          } else {
                                             NumberRef?.current[index]?.focus();
                                          }
                                       }
                                    }}
                                    showCustomNoOptions={true}
                                    openfrom="salesdaybook"
                                 />
                              </div>

                              {/* Amount Input */}
                              <div className="col-span-3">
                                 <Input
                                    ref={(el) => { NumberRef.current[index] = el }}
                                    type="number"
                                    value={entry.amount}
                                    onChange={(e) => handleEntryChange(index, 'amount', e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    // onKeyDown={(e) => {
                                    //    if (e.key === 'Enter' || e.key === 'Tab') {
                                    //       e.preventDefault();
                                    //       handleKeyDown(e, index)
                                    //       // typeRef?.current[index]?.focus();
                                    //    }
                                    // }}
                                    onFocus={() => handleAmountFocus(index)}
                                    // ref={index === formData.entries.length - 1 ? lastInputRef : null}
                                    placeholder="0.00"
                                    step="0.01"
                                    className={`rounded-md ${isDark
                                       ? 'bg-gray-700 border-gray-600 text-white'
                                       : 'bg-white border-gray-300 text-gray-900'
                                       }`}
                                 />
                              </div>

                              {/* Type Select */}
                              <div className="col-span-2">
                                 <Select
                                    // ref={(el) => { typeRef.current[index] = el }}
                                    value={entry.type}
                                    onChange={(e) => handleEntryChange(index, 'type', e.target.value)}
                                    // onKeyDown={(e) => {
                                    //    if (e.key === 'Enter' || e.key === 'Tab') {
                                    //       e.preventDefault();
                                    //       NumberRef?.current[index]?.focus();
                                    //    }
                                    // }}
                                    className={`rounded-md ${isDark
                                       ? 'bg-gray-700 border-gray-600 text-white'
                                       : 'bg-white border-gray-300 text-gray-900'
                                       }`}>
                                    <option value="1">Debit</option>
                                    <option value="0">Credit</option>
                                 </Select>
                              </div>

                              {/* Remove Button */}
                              <div className="col-span-2 flex items-center justify-center">
                                 {formData.entries.length > 1 && (
                                    <button
                                       onClick={() => removeEntry(index)}
                                       className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${isDark
                                          ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
                                          : 'bg-red-100 text-red-600 hover:bg-red-200'
                                          }`}>
                                       <CloseIcon boxSize={2.5} />
                                    </button>
                                 )}
                              </div>
                           </div>
                        ))}
                     </div>

                     {/* Add Account & Totals */}
                     <div className="flex flex-col md:flex-row justify-between items-start md:items-center mt-6 space-y-4 md:space-y-0">
                        <button
                           onClick={() => addEntry(true)}
                           className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${isDark
                              ? 'bg-blue-600 hover:bg-blue-700 text-white'
                              : 'bg-blue-500 hover:bg-blue-600 text-white'
                              }`}>
                           <AddIcon boxSize={3} />
                           <span>Add Account</span>
                        </button>

                        {/* Totals Display */}
                        <div className={`rounded-md p-4 min-w-[300px] ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                           <div className="grid grid-cols-3 gap-4 text-center">
                              <div>
                                 <div className={`text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                                    Debit
                                 </div>
                                 <div
                                    className={`text-lg font-semibold ${isDark ? 'text-green-300' : 'text-green-600'}`}>
                                    ₹{totalDebit.toFixed(2)}
                                 </div>
                              </div>

                              <div>
                                 <div className={`text-sm font-medium ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                                    Credit
                                 </div>
                                 <div className={`text-lg font-semibold ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
                                    ₹{totalCredit.toFixed(2)}
                                 </div>
                              </div>

                              {!isBalanced && (
                                 <div>
                                    <div className={`text-sm font-medium ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                                       Difference
                                    </div>
                                    <div
                                       className={`text-lg font-semibold ${isDark ? 'text-red-300' : 'text-red-600'}`}>
                                       ₹{difference.toFixed(2)}
                                    </div>
                                 </div>
                              )}
                           </div>
                        </div>
                     </div>
                  </div>

                  {/* Notes Section */}
                  <div
                     className={`rounded-lg p-4 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                        }`}>
                     <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Notes</h3>

                     <Textarea
                        ref={notesRef}
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        onKeyDown={(e) => {
                           if (e.key === 'Enter' || e.key === 'Tab') {
                              e.preventDefault();
                              if (e.shiftKey) {
                                 NumberRef?.current[formData?.length - 1]?.focus();
                              } else {
                                 const disabled = !isBalanced || formData.entries.some((e) => !e.account) || formData.entries.length < 2;
                                 if (!disabled) {
                                    saveRef?.current?.focus();
                                 }
                              }
                           }
                        }}
                        h="100px"
                        maxLength={250}
                        placeholder="Enter any additional notes..."
                        className={`rounded-md resize-none ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                           }`}
                     />
                     <div className={`text-right text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {formData.notes.length} / 250 characters
                     </div>
                  </div>
               </div>
            </ModalBody>

            {/* Footer */}
            <ModalFooter
               className={`py-4 px-6 border-t ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
               <div className="flex flex-col sm:flex-row justify-end items-center w-full space-y-3 sm:space-y-0">
                  <div className="flex space-x-3">
                     <button
                        onClick={onClose}
                        className={`px-6 py-2 rounded-md border font-medium transition-colors ${isDark
                           ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                           : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                           }`}>
                        Cancel
                     </button>

                     <button
                        ref={saveRef}
                        onClick={handleSave}
                        disabled={
                           !isBalanced || formData.entries.some((e) => !e.account) || formData.entries.length < 2
                        }
                        className={`px-6 py-2 rounded-md font-medium transition-colors ${isBalanced && !formData.entries.some((e) => !e.account) && formData.entries.length >= 2
                           ? isDark
                              ? 'bg-green-600 hover:bg-green-700 text-white'
                              : 'bg-green-500 hover:bg-green-600 text-white'
                           : isDark
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                           }`}>
                        {isEditMode ? 'Update Voucher' : 'Save Voucher'}
                     </button>
                  </div>
               </div>
            </ModalFooter>
         </ModalContent>

         <style jsx>{`
            .custom-scrollbar::-webkit-scrollbar {
               width: 6px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
               background: ${isDark ? '#1f2937' : '#f3f4f6'};
               border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
               background: ${isDark ? '#4b5563' : '#d1d5db'};
               border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
               background: ${isDark ? '#6b7280' : '#9ca3af'};
            }
         `}</style>
      </Modal>
   );
};

export default JournalVoucherForm;
